import { captureEvent } from "@sentry/react";
import {
  ChangeOutgoingInvoiceDatesDocument,
  type ChangeOutgoingInvoiceDatesMutation,
  type ChangeOutgoingInvoiceDatesMutationVariables,
  ChangeOutgoingProformaInvoiceDatesDocument,
  type ChangeOutgoingProformaInvoiceDatesMutation,
  type ChangeOutgoingProformaInvoiceDatesMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { DateRange } from "@src/utils/types";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable } from "mobx";
import { InvoiceTypeEnum } from "../../Create/store";

type ChangeDatesModalData = {
  id: string;
  invoice_type: InvoiceTypeEnum;
  issue_date: Date;
  date_of_supply?: Date | null;
  due_date: Date;
  paid_at?: Date | null;
  onUpdate: () => void;
};

export class ChangeDatesModalStore {
  private requiredIfOutgoingInvoice = ($: Date | undefined) => {
    if (
      this.modalState.additionalData?.invoice_type !== InvoiceTypeEnum.Outgoing
    )
      return null;
    return required($);
  };
  private paidAtRequired = ($: Date | undefined) => {
    if (!this.modalState.additionalData?.paid_at) return null;
    return required($);
  };

  form = new FormState({
    issue_date: new FieldState<Date | undefined>(undefined)
      .validators(required)
      .enableAutoValidation(),
    date_of_supply: new FieldState<Date | undefined>(undefined)
      .validators(this.requiredIfOutgoingInvoice)
      .enableAutoValidation(),
    due_date: new FieldState<Date | undefined>(undefined)
      .validators(required)
      .enableAutoValidation(),
    paid_at: new FieldState<Date | undefined>(undefined)
      .validators(this.paidAtRequired)
      .enableAutoValidation(),
  });

  private outgoingInvoiceMutator = new MutationHelper<
    ChangeOutgoingInvoiceDatesMutation,
    ChangeOutgoingInvoiceDatesMutationVariables
  >(ChangeOutgoingInvoiceDatesDocument);

  private proformaInvoiceMutator = new MutationHelper<
    ChangeOutgoingProformaInvoiceDatesMutation,
    ChangeOutgoingProformaInvoiceDatesMutationVariables
  >(ChangeOutgoingProformaInvoiceDatesDocument);

  modalState = new DisclosureState<ChangeDatesModalData>({
    onOpen: (data) => {
      if (!data) return;

      this.form.$.issue_date.onChange(data.issue_date);
      this.form.$.due_date.onChange(data.due_date);
      if (data.date_of_supply) {
        this.form.$.date_of_supply.onChange(data.date_of_supply);
      }
      if (data.paid_at) {
        this.form.$.paid_at.onChange(data.paid_at);
      }
    },
    onClose: () => {
      this.form.reset();
    },
  });

  constructor() {
    makeObservable(this);
  }

  @computed
  get isSubmitting(): boolean {
    return (
      this.outgoingInvoiceMutator.isLoading.value ||
      this.proformaInvoiceMutator.isLoading.value
    );
  }

  @computed
  get invoiceType(): InvoiceTypeEnum | undefined {
    return this.modalState.additionalData?.invoice_type;
  }

  @computed
  get showDateOfSupply(): boolean {
    return this.invoiceType === InvoiceTypeEnum.Outgoing;
  }

  @computed
  get showPaidAt(): boolean {
    return !!this.modalState.additionalData?.paid_at;
  }

  @action.bound
  handleIssueDateChange(value?: DateRange) {
    this.form.$.issue_date.onChange(value?.start);
  }

  @action.bound
  handleDateOfSupplyChange(value?: DateRange) {
    this.form.$.date_of_supply.onChange(value?.start);
  }

  handleDueDateChange = (value?: DateRange) => {
    this.form.$.due_date.onChange(value?.start);
  };

  handlePaidAtChange = (value?: DateRange) => {
    this.form.$.paid_at.onChange(value?.start);
  };

  @action.bound
  async updateDates(): Promise<void> {
    if (!this.modalState.additionalData) {
      appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: ChangeDatesModal additional data is missing.",
      });
      return;
    }

    const result = await this.form.validate();
    if (result.hasError) return;

    const id = this.modalState.additionalData.id;
    const issueDate = toApiDate(result.value.issue_date.value!);
    const dueDate = toApiDate(result.value.due_date.value!);
    const paidAtValue = this.form.$.paid_at.value;
    const paidAt = paidAtValue ? toApiDate(paidAtValue) : undefined;

    let success = false;

    if (this.modalState.additionalData.invoice_type === "outgoing") {
      const dateOfSupply = toApiDate(result.value.date_of_supply.value!);
      if (!dateOfSupply) return;

      const [data, error] = await this.outgoingInvoiceMutator.mutate({
        id,
        issue_date: issueDate,
        date_of_supply: dateOfSupply,
        due_date: dueDate,
        paid_at: paidAt,
      });

      success = !error && !!data?.changeOutgoingInvoiceDates;
    } else {
      const [data, error] = await this.proformaInvoiceMutator.mutate({
        id,
        issue_date: issueDate,
        due_date: dueDate,
        paid_at: paidAt,
      });

      success = !error && !!data?.changeOutgoingProformaInvoiceDates;
    }

    if (!success) return;
    this.modalState.additionalData.onUpdate();
    this.modalState.onClose();
  }
}
